import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
    format,
    getDay,
    getMonth,
    isFuture,
    isPast,
    isSameDay,
    isToday,
    isTomorrow,
    isYesterday,
    secondsToMilliseconds,
} from 'date-fns';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import {
    EpgProgram,
    MixedItem,
    RBProgram,
} from 'store/railsBuilder/reducer.types';
import { RecordingItem } from 'store/recordings/reducer.types';
import { selectLanguage } from 'store/user/selectors';

import { VodEditorial } from 'services/data-manager/types';

import getCorrectProgramStartAndEndTime from 'utils/getCorrectProgramStartAndEndTime';
import {
    isRecordingItem,
    selectRecordingMetadataBaseOnLanguage,
} from 'utils/recordings';

import { isLive } from 'screens/Epg/utils/program';

const {
    EPG: { DAY_TO_TRANSLATION, MONTH_TO_TRANSLATION },
} = LANGUAGE_KEYS;

export default function useProgramDateStr(
    item:
        | EpgProgram
        | RBProgram
        | MixedItem
        | VodEditorial
        | RecordingItem
        | undefined,
) {
    const { t } = useTranslation();
    const currentLanguage = useSelector(selectLanguage);

    return useMemo(() => {
        if (!item) {
            return '';
        }

        const { programStartTime, programEndTime } =
            getCorrectProgramStartAndEndTime(
                isRecordingItem(item)
                    ? selectRecordingMetadataBaseOnLanguage(
                          item.metadata,
                          currentLanguage,
                      )
                    : item,
            );

        const start = (item as MixedItem).startTime || programStartTime;
        const end = (item as MixedItem).endTime || programEndTime;

        let result = '';

        if (start && end) {
            const startDate = new Date(secondsToMilliseconds(start));
            const endDate = new Date(secondsToMilliseconds(end));

            let endDateString = '';

            if (!isSameDay(startDate, endDate)) {
                if (isPast(endDate)) {
                    if (isToday(endDate)) {
                        endDateString += `${t('DAY_NAME_TODAY')}`;
                    } else if (isYesterday(endDate)) {
                        endDateString += `${t('DAY_NAME_YESTERDAY')}`;
                    } else {
                        endDateString += `${t(
                            DAY_TO_TRANSLATION[getDay(endDate)],
                        )} ${format(endDate, 'dd')} ${t(
                            MONTH_TO_TRANSLATION[getMonth(endDate)],
                        )}`;
                    }
                } else if (isFuture(endDate)) {
                    if (isToday(endDate)) {
                        endDateString += `${t('DAY_NAME_TODAY')}`;
                    } else if (isTomorrow(endDate)) {
                        endDateString += `${t('DAY_NAME_TOMORROW')}`;
                    } else {
                        endDateString += `${t(
                            DAY_TO_TRANSLATION[getDay(endDate)],
                        )} ${format(endDate, 'dd')} ${t(
                            MONTH_TO_TRANSLATION[getMonth(endDate)],
                        )}`;
                    }
                }
            }

            const time = `${format(startDate, 'HH:mm')} - ${endDateString}${
                endDateString ? ', ' : ''
            }${format(endDate, 'HH:mm')}  `;

            if (isPast(startDate)) {
                if (isToday(startDate)) {
                    result += `${t('DAY_NAME_TODAY')}, ${time}`;
                } else if (isYesterday(startDate)) {
                    result += `${t('DAY_NAME_YESTERDAY')}, ${time}`;
                } else {
                    result += `${t(
                        DAY_TO_TRANSLATION[getDay(startDate)],
                    )} ${format(startDate, 'dd')} ${t(
                        MONTH_TO_TRANSLATION[getMonth(startDate)],
                    )}, ${time}`;
                }
            } else if (
                isLive(
                    isRecordingItem(item)
                        ? selectRecordingMetadataBaseOnLanguage(
                              item.metadata,
                              currentLanguage,
                          )
                        : item,
                )
            ) {
                result += `${t('DAY_NAME_TODAY')} ${time}`;
            } else if (isFuture(startDate)) {
                if (isToday(startDate)) {
                    result += `${t('DAY_NAME_TODAY')} ${time}`;
                } else if (isTomorrow(startDate)) {
                    result += `${t('DAY_NAME_TOMORROW')} ${time}`;
                } else {
                    result += `${t(
                        DAY_TO_TRANSLATION[getDay(startDate)],
                    )} ${format(startDate, 'dd')} ${t(
                        MONTH_TO_TRANSLATION[getMonth(startDate)],
                    )} ${time}`;
                }
            }
        }

        return result;
    }, [t, item]);
}
